import React, { createContext, useCallback, useContext, useLayoutEffect } from 'react';
import axios from 'axios';
import api from '../api/api';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  beginOperatingContext, confirmOperatingContext, failOperatingContext, getOperatingContext,
  OperatingSelection, OPERATING_GROUP_STORAGE_KEY,
} from '../utils/operatingContextLifecycle';
import { contextError } from '../utils/operatingContext';

const Actions = createContext({ retry: () => {}, select: async (_groupId: string) => {} });
export const useOperatingContextActions = () => useContext(Actions);

const OperatingContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = useAppSelector(state => state.auth.token);
  const dispatch = useAppDispatch();

  const bootstrap = useCallback(async (groupId?: string) => {
    const generation = beginOperatingContext(token);
    dispatch({ type: 'operatingContext/reset' });
    if (!token) {
      localStorage.removeItem(OPERATING_GROUP_STORAGE_KEY);
      return;
    }
    try {
      let expectedGroupId = groupId;
      const confirmRequestedSelection = (data: OperatingSelection) => {
        if (!expectedGroupId) return true;
        const matches = expectedGroupId === 'all'
          ? data.allGroupsOverview === true && data.canViewAllGroups === true && data.selectedGroupId === null
          : data.allGroupsOverview !== true && String(data.selectedGroupId) === expectedGroupId
            && data.groups.some(group => String(group.id) === expectedGroupId);
        if (!matches) {
          failOperatingContext(generation,
            'Вибраний контекст змінено паралельно. Роботу заблоковано. Оновіть контекст і перевірте поточний вибір перед продовженням.');
        }
        return matches;
      };
      if (groupId) {
        await api.put('/operating-contexts/selection', groupId === 'all'
          ? { allGroupsOverview: true } : { groupId: Number(groupId) });
      }
      let { data } = await api.get<OperatingSelection>('/operating-contexts/selection');
      if (getOperatingContext().generation !== generation) return;
      if (!confirmRequestedSelection(data)) return;
      if (data.selectedGroupId === null && !data.allGroupsOverview && data.groups.length === 1) {
        expectedGroupId = String(data.groups[0].id);
        await api.put('/operating-contexts/selection', { groupId: Number(expectedGroupId) });
        data = (await api.get<OperatingSelection>('/operating-contexts/selection')).data;
        if (getOperatingContext().generation !== generation || !confirmRequestedSelection(data)) return;
      }
      confirmOperatingContext(generation, data);
    } catch (error) {
      if (!axios.isCancel(error)) failOperatingContext(generation, contextError(error));
    }
  }, [dispatch, token]);

  useLayoutEffect(() => {
    void bootstrap();
    return () => { beginOperatingContext(null); };
  }, [bootstrap]);

  useLayoutEffect(() => {
    const changed = (event: StorageEvent) => {
      if (event.key === 'token' || event.key === null ||
          (event.key === OPERATING_GROUP_STORAGE_KEY && event.newValue !== getOperatingContext().groupId)) {
        const generation = beginOperatingContext(null);
        dispatch({ type: 'operatingContext/reset' });
        failOperatingContext(generation, 'Контекст змінено в іншій вкладці. Оновіть сторінку перед продовженням.');
        if (window.confirm('Робочу групу або користувача змінено в іншій вкладці. Перезавантажити цю вкладку? Незбережені дані буде втрачено.')) {
          window.location.reload();
        }
      }
    };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, [dispatch]);

  const retry = () => {
    if (token !== localStorage.getItem('token')) window.location.reload();
    else void bootstrap();
  };
  return <Actions.Provider value={{ retry, select: bootstrap }}>{children}</Actions.Provider>;
};

export default OperatingContextProvider;
